import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';

const BlackbirdAI = () => {
  return (
    <div className="blackbird-ai-page">
      {/* Dark Header for this page */}
      <header className="fixed top-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl z-50 border-b border-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_blackbird-redesign/artifacts/iuwp2epg_logo-trade-black-svg.svg" 
                alt="Blackbird Trade Group" 
                className="h-14 brightness-0 invert"
              />
            </Link>
            <nav className="hidden lg:flex items-center gap-10">
              <Link to="/about" className="text-sm font-semibold text-gray-200 hover:text-white transition-colors">About</Link>
              <Link to="/services" className="text-sm font-semibold text-gray-200 hover:text-white transition-colors">Services</Link>
              <Link to="/resources" className="text-sm font-semibold text-gray-200 hover:text-white transition-colors">Resources</Link>
              <Link to="/blog" className="text-sm font-semibold text-gray-200 hover:text-white transition-colors">Insights</Link>
              <Link to="/blackbird-ai" className="text-sm font-semibold text-white transition-colors">Blackbird AI</Link>
              <Link to="/contact">
                <Button className="bg-white text-slate-900 hover:bg-gray-100 px-7 h-11 text-sm font-semibold">
                  Contact Us
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-5 py-2 mb-8">
              <Sparkles className="h-4 w-4 text-white" />
              <span className="text-sm font-medium text-white">Introducing Blackbird AI</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              Global Trade Decision Engine
            </h1>
            <p className="text-xl lg:text-2xl text-gray-200 leading-relaxed mb-12 max-w-3xl mx-auto">
              AI-powered trade compliance analysis covering product classification, export controls, sanctions screening, and regulatory requirements across 62+ jurisdictions.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-gray-100 px-10 h-14 text-base font-semibold shadow-xl"
              onClick={() => window.open('https://blackbird-trade.com', '_blank')}
            >
              Visit Blackbird AI Platform
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* What It Does */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8 text-center leading-tight">
              Transform complex global regulations into clear, actionable intelligence
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed text-center mb-16">
              Blackbird AI delivers accurate product classifications, regulatory requirements, and compliance decisions in one unified workflow.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Product Classification',
                  description: 'Accurate classification across HS, HTS, and export control regimes with structured legal reasoning'
                },
                {
                  title: 'Regulatory Requirements',
                  description: 'Know exactly which regulations apply and what they require across import and export'
                },
                {
                  title: 'Risk & Screening',
                  description: 'Screen for denied parties, sanctions, and restrictions automatically'
                },
                {
                  title: 'Multi-Jurisdiction Analysis',
                  description: 'See where regulations from multiple countries intersect in one view'
                },
                {
                  title: 'Documentation & Review',
                  description: 'Generate commercial invoices, SOPs, and export-ready documentation'
                },
                {
                  title: 'Batch Processing',
                  description: 'Classify thousands of products at once—up to 5,000 in a single batch'
                }
              ].map((feature, i) => (
                <div key={i} className="border-l-4 border-slate-600 pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="text-5xl font-bold text-slate-700 mb-3">62+</div>
              <div className="text-lg text-gray-600">Countries Covered</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-slate-700 mb-3">200+</div>
              <div className="text-lg text-gray-600">Regulatory Authorities</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-slate-700 mb-3">One</div>
              <div className="text-lg text-gray-600">Unified Platform</div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Built for global trade professionals
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Blackbird AI supports teams working across international trade operations—from compliance to logistics to advisory.
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              {[
                'Trade compliance teams',
                'Logistics and supply chain managers',
                'Import/export operations specialists',
                'Trade consultants and advisors',
                'Trade directors and managers',
                'Cross-functional trade teams'
              ].map((role, i) => (
                <div key={i} className="flex items-center text-gray-700 text-lg">
                  <div className="w-2 h-2 bg-slate-600 rounded-full mr-4"></div>
                  {role}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
            Ready to see how it works?
          </h2>
          <p className="text-xl text-gray-200 mb-12 max-w-2xl mx-auto leading-relaxed">
            Access the full Blackbird AI platform to run trade analyses, get classifications, and receive compliance decisions.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-slate-900 hover:bg-gray-100 px-10 h-14 text-base font-semibold shadow-xl"
            onClick={() => window.open('https://blackbird-trade.com', '_blank')}
          >
            Visit Blackbird AI Platform
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlackbirdAI;